import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { signOut } from '../lib/auth'
import { showToast } from '../components/Toast'
import { SERVICE_LABEL } from '../lib/orders'
import { shortDate, isoDate } from '../lib/dates'
import OrderPanel from '../components/OrderPanel'
import GroupOrderPanel from '../components/GroupOrderPanel'
import AdminCatalog from './AdminCatalog'
import StripeAuditPanel from '../components/StripeAuditPanel'
import {
  countArchivedOrders,
  fmtMoney,
  isCurrentUserAdmin,
  listActiveOrders,
  listArchivedOrders,
  patchOrder,
} from '../lib/admin'
import { sendDelivered, sendPickedUp } from '../lib/emailTemplate'
import { sendSms } from '../lib/sms'
import { getDeliveryProofUrl, uploadDeliveryProof, uploadDeliveryProofForPickup } from '../lib/deliveryPhoto'
import { supabase } from '../lib/supabase'

// ══════════════════════════════════════════════════════════════════════
// Admin dashboard — spec §3 tabs, §4 lock, §5 checkout.
//
// Tabs (in this order):
//   Orders            → status IN (booked, picked_up)   · grouped by slot
//   Processing        → status = processing             · flat
//   Out for delivery  → status = out_for_delivery       · grouped by slot
//
// History + Catalog + Stripe are discreet links under the tab bar, never
// tabs: they are tools, not a place where orders are worked.
//
// No status dropdown anywhere. Each card carries one action button that
// advances the order along its single-direction path. Everything else in
// the ticket is a consequence: the ticket has no status action, no
// payment button — it's driven from the cards.
// ══════════════════════════════════════════════════════════════════════

const SLOT_ORDER = ['morning', 'evening']
const SLOT_LABELS = { morning: 'Morning · 9–11 AM', evening: 'Evening · 6–9 PM' }

export default function Admin() {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [adminState, setAdminState] = useState('checking')

  // View — one of: 'tabs' | 'history' | 'catalog' | 'stripe'
  const [view, setView] = useState('tabs')
  const [tab, setTab] = useState('orders') // 'orders' | 'processing' | 'out'
  const [archivedCount, setArchivedCount] = useState(0)

  // Counts for the 3 tabs, refreshed together.
  const [counts, setCounts] = useState({ orders: 0, processing: 0, out: 0 })

  const [panelOrder, setPanelOrder] = useState(null)
  const [panelArchived, setPanelArchived] = useState(false)
  // Bumped every time OrderPanel writes — forces TabContent / HistoryView
  // to re-fetch so the underlying card matches what the drawer just saved.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (authLoading) return
    if (!user) { navigate('/login', { replace: true }); return }
    let alive = true
    ;(async () => {
      const ok = await isCurrentUserAdmin()
      if (!alive) return
      if (!ok) {
        // Non-admins never see the /staff surface — silent bounce to their
        // own account page. No "Access denied" splash.
        navigate('/account', { replace: true })
        return
      }
      setAdminState('ok')
      try { setArchivedCount(await countArchivedOrders()) } catch { /* ignore */ }
    })()
    return () => { alive = false }
  }, [authLoading, user, navigate])

  const refreshArchivedCount = useCallback(async () => {
    try { setArchivedCount(await countArchivedOrders()) } catch { /* ignore */ }
  }, [])

  // Live sync — when the DB writes an orders row (webhook auto-marking a
  // paid invoice, another admin cancelling from another tab, etc.) we
  // bump reloadKey so TabContent + HistoryView re-fetch. Bumped ONLY on
  // real updates; the panel's own writes already bump via onChanged.
  useEffect(() => {
    if (adminState !== 'ok') return
    const channel = supabase
      .channel('admin-orders-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        () => {
          setReloadKey((k) => k + 1)
          refreshArchivedCount()
        },
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [adminState, refreshArchivedCount])

  if (authLoading || adminState === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-[13px] font-bold text-ink-2">Loading…</div>
      </div>
    )
  }
  if (adminState === 'denied') return <AccessDenied />

  const openPanel = (row, archived = false) => {
    setPanelOrder(row); setPanelArchived(archived)
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <AdminHeader email={user?.email} />

      <div className="mx-auto max-w-6xl px-4 pt-3">
        {/* Tab bar always shows Orders / Processing / Out for delivery */}
        {view === 'tabs' && (
          <>
            <div className="mb-2 flex gap-2">
              <div className="flex flex-1 gap-1 rounded-lg bg-white p-1 shadow-sm">
                <TabButton active={tab === 'orders'} onClick={() => setTab('orders')}>
                  Orders <TabCount n={counts.orders} />
                </TabButton>
                <TabButton active={tab === 'processing'} onClick={() => setTab('processing')}>
                  Processing <TabCount n={counts.processing} />
                </TabButton>
                <TabButton active={tab === 'out'} onClick={() => setTab('out')}>
                  Out for delivery <TabCount n={counts.out} />
                </TabButton>
              </div>
              <button
                type="button"
                onClick={() => navigate('/staff/orders/new')}
                className="shrink-0 rounded-lg bg-navy px-3 py-2 font-bricolage text-[12px] font-extrabold text-white shadow-sm"
                aria-label="Create a new order on a customer's behalf"
              >
                + New Order
              </button>
            </div>
            <SubLinks
              archivedCount={archivedCount}
              onHistory={() => setView('history')}
              onCatalog={() => setView('catalog')}
              onStripe={() => setView('stripe')}
            />
          </>
        )}

        {/* Un seul bloc de retour pour toutes les vues outils : il y en
            avait deux identiques, une troisième copie n'avait pas de sens. */}
        {view !== 'tabs' && (
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setView('tabs')}
              className="text-[12px] font-bold text-navy underline"
            >
              ← Active orders
            </button>
          </div>
        )}

        {view === 'tabs' && (
          <TabContent
            tab={tab}
            reloadKey={reloadKey}
            openPanel={openPanel}
            onCountsChange={(next) => setCounts(next)}
            onArchivedChange={refreshArchivedCount}
          />
        )}
        {view === 'history' && (
          <HistoryView reloadKey={reloadKey} openPanel={(o) => openPanel(o, true)} />
        )}
        {view === 'catalog' && <AdminCatalog />}
        {view === 'stripe' && <StripeAuditPanel />}
      </div>

      {/* A multi-service pickup opens ONE ticket with a weigh block per
          order. A standalone order keeps the existing ticket, untouched. */}
      {panelOrder?.pickup_group_id ? (
        <GroupOrderPanel
          open={!!panelOrder}
          order={panelOrder}
          onClose={() => setPanelOrder(null)}
          onChanged={() => {
            refreshArchivedCount()
            setReloadKey((k) => k + 1)
          }}
        />
      ) : (
        <OrderPanel
          open={!!panelOrder}
          order={panelOrder}
          archived={panelArchived}
          onClose={() => setPanelOrder(null)}
          onChanged={(next) => {
            setPanelOrder(next)
            refreshArchivedCount()
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </div>
  )
}

function AccessDenied() {
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red/10 text-2xl">🚫</div>
      <h1 className="font-bricolage text-[20px] font-extrabold text-navy">Access denied</h1>
      <p className="mt-2 text-[13px] text-ink-2">
        This area is for staff only. If you think that&apos;s a mistake, text (954) 900-9969.
      </p>
      <Link to="/order" className="mt-6 text-[13px] font-bold text-aqua">Back to the app →</Link>
    </div>
  )
}

function AdminHeader({ email }) {
  const navigate = useNavigate()
  async function handleSignOut() {
    try { await signOut() } catch { /* ignore */ }
    navigate('/login', { replace: true })
  }
  return (
    <header className="sticky top-0 z-30 flex items-start justify-between gap-3 border-b border-line bg-white px-4 py-3">
      <Link to="/admin" className="flex items-center gap-2" aria-label="Ghost Laundry Admin">
        <img src="/logo-ghost.png" alt="" aria-hidden="true" className="h-9 w-9 rounded-md" />
        <div>
          <div className="font-bricolage text-[13px] font-extrabold leading-none text-navy">
            GHOSTLAUNDRY
          </div>
          <div className="mt-0.5 text-[9px] font-bold uppercase tracking-widest text-aqua">
            Admin
          </div>
        </div>
      </Link>
      <div className="flex flex-col items-end text-right text-[12px] leading-tight">
        <div className="text-ink-2">{email || ''}</div>
        <button type="button" onClick={handleSignOut} className="mt-0.5 text-ink-2 hover:text-navy underline">
          Sign out
        </button>
      </div>
    </header>
  )
}

function SubLinks({ archivedCount, onHistory, onCatalog, onStripe }) {
  return (
    <div className="mb-4 flex items-center gap-3 text-[12px] text-ink-2">
      <button type="button" onClick={onHistory} className="underline hover:text-navy">
        History ({archivedCount})
      </button>
      <span aria-hidden>·</span>
      <button type="button" onClick={onCatalog} className="underline hover:text-navy">
        Catalog
      </button>
      <span aria-hidden>·</span>
      <button type="button" onClick={onStripe} className="underline hover:text-navy">
        Stripe
      </button>
    </div>
  )
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-md py-2 text-[13px] font-extrabold transition ${
        active ? 'bg-navy text-white' : 'text-ink-2 hover:bg-[#F3F4F6]'
      }`}
    >
      {children}
    </button>
  )
}
function TabCount({ n }) {
  if (n == null) return null
  return <span className="ml-1 text-[10px] opacity-70">{n}</span>
}

// ══════════════════════════════════════════════════════════════════════
// TabContent — loads active orders once, splits them into the 3 tabs.
// ══════════════════════════════════════════════════════════════════════

function TabContent({ tab, reloadKey = 0, openPanel, onCountsChange, onArchivedChange }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const refresh = useCallback(async () => {
    setLoading(true); setError('')
    try {
      // listActiveOrders returns everything not-yet-archived; we split by status.
      const data = await listActiveOrders()
      setRows(data)
    } catch (err) {
      setError(err?.message || 'Could not load orders')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh, reloadKey])

  // A ticket only leaves the board once it's actually delivered
  // (delivered_at set) or cancelled. Payment never closes a ticket — a paid
  // order still has to go back to the customer. The `out` bucket also
  // catches any row whose status falls outside the four known ones, so a
  // non-delivered ticket can never end up invisible.
  const groups = useMemo(() => {
    const live = rows.filter((r) => !r.delivered_at && r.status !== 'cancelled')
    const orders     = live.filter((r) => r.status === 'booked' || r.status === 'picked_up')
    const processing = live.filter((r) => r.status === 'processing')
    const out        = live.filter((r) =>
      r.status === 'out_for_delivery' ||
      !['booked', 'picked_up', 'processing'].includes(r.status),
    )
    return { orders, processing, out }
  }, [rows])

  useEffect(() => {
    onCountsChange({
      orders: groups.orders.length,
      processing: groups.processing.length,
      out: groups.out.length,
    })
  }, [groups, onCountsChange])

  if (loading) return <div className="rounded-xl bg-white p-6 text-center text-[13px] text-ink-2">Loading…</div>
  if (error) return <div className="rounded-xl border border-red bg-red/10 p-4 text-[12px] font-bold text-red">{error}</div>

  const list = tab === 'orders' ? groups.orders : tab === 'processing' ? groups.processing : groups.out

  // Empty tab → render nothing. No title, no dash, no panel.
  if (list.length === 0) return null

  // Orders and Out for delivery are grouped by slot (Morning / Evening).
  // Processing is flat.
  const grouped = tab === 'processing'
  const slotField = tab === 'out' ? 'return_slot' : 'pickup_slot'

  return (
    <div className="space-y-6">
      {grouped ? (
        <CardList groupPickups rows={list} openPanel={openPanel} onDone={() => { refresh(); onArchivedChange?.() }} />
      ) : (
        <>
          {SLOT_ORDER.map((slot) => {
            const items = list.filter((r) => r[slotField] === slot)
            if (items.length === 0) return null // empty slot → hidden
            return (
              <section key={slot}>
                <h2 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-2">
                  {SLOT_LABELS[slot]}
                  <span className="ml-2 text-ink-3">· {items.length}</span>
                </h2>
                <CardList groupPickups rows={items} openPanel={openPanel} onDone={() => { refresh(); onArchivedChange?.() }} />
              </section>
            )
          })}
          {/* Anything without a slot (e.g. "confirmed when ready" returns)
              still has to show up — otherwise the ticket is invisible. */}
          {(() => {
            const rest = list.filter((r) => !SLOT_ORDER.includes(r[slotField]))
            if (rest.length === 0) return null
            return (
              <section>
                <h2 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-2">
                  No time set
                  <span className="ml-2 text-ink-3">· {rest.length}</span>
                </h2>
                <CardList groupPickups rows={rest} openPanel={openPanel} onDone={() => { refresh(); onArchivedChange?.() }} />
              </section>
            )
          })()}
        </>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Card
// ══════════════════════════════════════════════════════════════════════

function CardList({ rows, openPanel, onDone, groupPickups = false }) {
  // On the Orders board a multi-service pickup is ONE stop for the driver,
  // so its orders share a card. In Processing every ticket stands alone —
  // different weights, different prices, different bills.
  const entries = groupPickups ? groupByPickup(rows) : rows.map((r) => ({ key: r.order_number, rows: [r] }))
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {entries.map((e) =>
        e.rows.length > 1
          ? <PickupCard key={e.key} rows={e.rows} openPanel={openPanel} onDone={onDone} />
          : <Card key={e.key} row={e.rows[0]} openPanel={openPanel} onDone={onDone} />,
      )}
    </div>
  )
}

function groupByPickup(rows) {
  const out = []
  const at = new Map()
  for (const r of rows) {
    if (!r.pickup_group_id) { out.push({ key: r.order_number, rows: [r] }); continue }
    const i = at.get(r.pickup_group_id)
    if (i == null) { at.set(r.pickup_group_id, out.length); out.push({ key: r.pickup_group_id, rows: [r] }) }
    else out[i].rows.push(r)
  }
  return out
}

// One stop, several orders. Actions apply to every order in the group so the
// driver taps once; the customer gets a single notification, not one per bag.
function PickupCard({ rows, openPanel, onDone }) {
  const head = rows[0]
  const address = [head.address_line1, head.city, head.state, head.zip].filter(Boolean).join(', ')
  const mapsHref = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null
  const totalCents = rows.reduce((s, r) => s + Number(r.final_total_cents || 0), 0)

  return (
    <article className="rounded-xl p-3 transition" style={cardStyle(head)}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-bricolage text-[13px] font-extrabold text-navy">
          {rows.map((r) => `#${r.order_number}`).join(' + ')}
        </div>
        <span className="rounded-full bg-navy/10 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-navy">
          {rows.length} services
        </span>
      </div>

      {/* Same signal as a plain card — it was missing here. */}
      {rows.every((r) => r.is_paid) && !head.delivered_at && (
        <div className="mt-1 inline-block rounded-full bg-green/10 px-2 py-0.5 text-[10px] font-extrabold text-green">
          ✓ Paid — awaiting delivery
        </div>
      )}

      <div className="mt-1 text-[13px] font-bold text-navy">{head.customer_name || '—'}</div>

      <ul className="mt-1 space-y-0.5">
        {rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => openPanel(r)}
              className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-0.5 text-left text-[11px] hover:bg-black/5"
            >
              <span className="truncate text-ink-2">
                {SERVICE_LABEL[r.service] || r.service} · {r.bags} bag{String(r.bags) === '1' ? '' : 's'}
              </span>
              <span aria-hidden className="text-ink-3">›</span>
            </button>
          </li>
        ))}
      </ul>

      {head.hotel_name && (
        <div className="mt-1 truncate text-[11px] font-bold text-navy">
          🏨 {head.hotel_name}{head.room_number ? ` · Room ${head.room_number}` : ''}
        </div>
      )}
      {mapsHref && (
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 block truncate text-[11px] text-aqua hover:underline"
        >
          📍 {address}
        </a>
      )}
      {totalCents > 0 && (
        <div className="mt-1 text-[11px] text-ink-2">
          Group total <b className="text-navy">{fmtMoney(totalCents)}</b>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        {head.customer_phone && (
          <a
            href={`tel:${head.customer_phone}`}
            className="rounded-lg border border-navy bg-white px-3 py-1.5 text-[11px] font-extrabold text-navy"
          >
            📞 Call
          </a>
        )}
        {/* One trip, one proof — written onto every order of the pickup. */}
        {head.status === 'out_for_delivery' && (
          <PhotoControl rows={rows} onDone={onDone} />
        )}
        <GroupAction rows={rows} openPanel={openPanel} onDone={onDone} />
      </div>
    </article>
  )
}

// Advances every order of the pickup together. Customer-facing email/SMS go
// out once for the whole stop — one pickup, one message.
function GroupAction({ rows, openPanel, onDone }) {
  const [busy, setBusy] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const head = rows[0]

  // A stray tap must not leave the card armed. Same 3s as the plain card.
  useEffect(() => {
    if (!confirmCancel) return
    const t = setTimeout(() => setConfirmCancel(false), 3000)
    return () => clearTimeout(t)
  }, [confirmCancel])
  // Every button below advances the whole group, so it may only appear when
  // the group actually is at one stage. Mixed stages ⇒ open the ticket.
  const uniform = rows.every((r) => r.status === head.status)
  const cls = 'ml-auto rounded-lg bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white disabled:opacity-50'

  async function advanceAll(patch, label, after) {
    setBusy(true)
    try {
      await Promise.all(rows.map((r) => patchOrder(r.id, patch)))
      await after?.()
      onDone?.()
      showToast(label)
    } catch (err) {
      showToast(err?.message || 'Update failed')
    } finally {
      setBusy(false)
    }
  }

  if (!uniform) {
    return (
      <button type="button" onClick={() => openPanel(head)} className={cls}>
        Open ticket
      </button>
    )
  }

  if (head.status === 'booked') {
    return (
      <div className="ml-auto flex items-center gap-2">
        {/* Parity with the plain card, which has always offered Cancel here. */}
        {confirmCancel ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => advanceAll({ status: 'cancelled' }, 'Pickup cancelled')}
            className="rounded-lg bg-red px-3 py-1.5 text-[11px] font-extrabold text-white disabled:opacity-50"
          >
            Tap again to confirm
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmCancel(true)}
            className="rounded-lg border border-red bg-white px-3 py-1.5 text-[11px] font-extrabold text-red hover:bg-red/5"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => advanceAll(
            { status: 'picked_up', picked_up_at: new Date().toISOString() },
            'Pickup marked picked up',
            async () => { sendPickedUp(head.id) },
          )}
          className="rounded-lg bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white disabled:opacity-50"
        >
          Mark picked up
        </button>
      </div>
    )
  }
  if (head.status === 'picked_up') {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => advanceAll({ status: 'processing' }, 'Processing started')}
        className={cls}
      >
        Start processing
      </button>
    )
  }

  // One pickup = one bill, so the whole group waits on the same payment
  // before it goes back out. `is_paid` is derived per order but the invoice
  // covers the group, so every row flips together.
  const allPaid = rows.every((r) => r.is_paid)

  if (head.status === 'processing' && !allPaid) {
    return (
      <button type="button" onClick={() => openPanel(head)} className={cls}>
        Checkout
      </button>
    )
  }
  if (head.status === 'processing' && allPaid) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => advanceAll({ status: 'out_for_delivery' }, 'Out for delivery')}
        className={cls}
      >
        Send out for delivery
      </button>
    )
  }
  if (head.status === 'out_for_delivery') {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => advanceAll(
          { status: 'delivered', delivered_at: new Date().toISOString() },
          'Delivered',
          // Everything came back in one trip: ONE receipt, ONE text. The
          // email itself covers every order of the group.
          async () => {
            sendDelivered(head.id)
            try { await sendSms(head.id, 'delivered') } catch { /* ignore */ }
          },
        )}
        className={cls}
      >
        Mark delivered
      </button>
    )
  }
  return <span className="ml-auto text-[10px] text-ink-3">Open the ticket</span>
}

// Card color per spec §3 — booked/processing-unpaid/out white, picked_up
// yellow, processing paid green. Nothing else, no badge.
function cardStyle(row) {
  if (row.status === 'picked_up') {
    return { background: '#FEF6DF', border: '1px solid #F0DCA0' }
  }
  if (row.status === 'processing' && row.is_paid) {
    return { background: '#E9F9EF', border: '1px solid #B6E7C7' }
  }
  return { background: '#fff', border: '1px solid #E5E7EB' }
}

function Card({ row, openPanel, onDone }) {
  const address = [row.address_line1, row.city, row.state, row.zip].filter(Boolean).join(', ')
  const mapsHref = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null

  return (
    <article
      onClick={() => openPanel(row)}
      className="cursor-pointer rounded-xl p-3 transition"
      style={cardStyle(row)}
    >
      <div className="flex items-baseline justify-between">
        <div className="font-bricolage text-[13px] font-extrabold text-navy">#{row.order_number}</div>
        <div className="text-[10px] uppercase tracking-widest text-ink-3">
          {SERVICE_LABEL[row.service] || row.service}
        </div>
      </div>
      {row.is_paid && !row.delivered_at && (
        <div className="mt-1 inline-block rounded-full bg-green/10 px-2 py-0.5 text-[10px] font-extrabold text-green">
          ✓ Paid — awaiting delivery
        </div>
      )}
      <div className="mt-1 text-[13px] font-bold text-navy">{row.customer_name || '—'}</div>
      <div className="mt-0.5 text-[11px] text-ink-2">{row.bags} bag{row.bags === '1' ? '' : 's'}</div>
      {row.hotel_name && (
        <div className="mt-1 truncate text-[11px] font-bold text-navy">
          🏨 {row.hotel_name}{row.room_number ? ` · Room ${row.room_number}` : ''}
        </div>
      )}
      {mapsHref && (
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mt-1 block truncate text-[11px] text-aqua hover:underline"
        >
          📍 {address}
        </a>
      )}

      <div className="mt-3 flex items-center gap-2">
        {row.customer_phone && (
          <a
            href={`tel:${row.customer_phone}`}
            onClick={(e) => e.stopPropagation()}
            className="rounded-lg border border-navy bg-white px-3 py-1.5 text-[11px] font-extrabold text-navy"
          >
            📞 Call
          </a>
        )}
        {row.status === 'out_for_delivery' && (
          <PhotoControl rows={[row]} onDone={onDone} />
        )}
        <CardAction row={row} onDone={onDone} openPanel={openPanel} />
      </div>
    </article>
  )
}

// 📷 button on Out for delivery cards. Compresses to 1200 px / q=0.7
// before upload. Replaced by a 38×38 thumbnail once a photo exists;
// clicking the thumbnail lets the driver replace it, until delivery.
//
// `rows` is ALWAYS an array — one order for a plain card, the whole pickup
// for a grouped one. A pickup came back in one trip, so it gets ONE photo,
// written onto every order of the group.
function PhotoControl({ rows, onDone }) {
  const head = rows[0]
  const groupId = head.pickup_group_id || null
  const [thumbUrl, setThumbUrl] = useState(null)
  const [busy, setBusy] = useState(false)
  const inputId = `photo-${head.id}`
  // Once delivered the proof is evidence, not a draft: look, don't replace.
  const locked = rows.some((r) => r.status === 'delivered' || r.delivered_at)
  const photoPath = rows.find((r) => r.delivery_photo_path)?.delivery_photo_path || null

  useEffect(() => {
    let alive = true
    if (photoPath) {
      getDeliveryProofUrl(photoPath).then((url) => {
        if (alive) setThumbUrl(url)
      })
    } else {
      setThumbUrl(null)
    }
    return () => { alive = false }
  }, [photoPath])

  async function handleFile(e) {
    e.stopPropagation()
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    try {
      if (groupId) {
        await uploadDeliveryProofForPickup(head.id, groupId, file)
      } else {
        await uploadDeliveryProof(head.id, file)
      }
      onDone?.()
      showToast(groupId ? `Photo saved for all ${rows.length} services` : 'Photo saved')
    } catch (err) {
      showToast(err?.message || 'Upload failed')
    } finally {
      setBusy(false)
      // Reset the input so picking the same file again fires onChange.
      e.target.value = ''
    }
  }

  const thumb = (
    <img
      src={thumbUrl}
      alt="Delivery"
      width={38}
      height={38}
      className="h-[38px] w-[38px] rounded-md object-cover"
      style={{ border: '1px solid rgba(11,32,56,0.15)' }}
    />
  )

  if (locked) {
    return thumbUrl
      ? <span onClick={(e) => e.stopPropagation()} title="Delivery photo">{thumb}</span>
      : null
  }

  return (
    <label
      htmlFor={inputId}
      onClick={(e) => e.stopPropagation()}
      className={`cursor-pointer ${busy ? 'opacity-60' : ''}`}
      aria-label={thumbUrl ? 'Replace delivery photo' : 'Attach delivery photo'}
      title={thumbUrl ? 'Replace delivery photo' : 'Attach delivery photo'}
    >
      {thumbUrl ? thumb : (
        <span className="inline-flex items-center rounded-lg border border-navy bg-white px-3 py-1.5 text-[11px] font-extrabold text-navy">
          📷 Photo
        </span>
      )}
      <input
        id={inputId}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        disabled={busy}
      />
    </label>
  )
}

// Single-action button per card. Sens unique — no dropdown.
function CardAction({ row, onDone, openPanel }) {
  const paid = !!row.is_paid
  const [confirmCancel, setConfirmCancel] = useState(false)

  // Auto-dismiss the confirm state after 3s so a stray tap doesn't stick.
  useEffect(() => {
    if (!confirmCancel) return
    const t = setTimeout(() => setConfirmCancel(false), 3000)
    return () => clearTimeout(t)
  }, [confirmCancel])

  async function advance(patch, label) {
    try {
      await patchOrder(row.id, patch)
      onDone?.()
      showToast(label)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[card] patchOrder failed', { patch, err })
      showToast(err?.message || err?.hint || err?.details || 'Update failed')
    }
  }

  // Fire the "picked_up" email (spec §6). The Edge Function re-reads the
  // order server-side — the client just says "send this one".
  async function markPickedUp() {
    await advance({ status: 'picked_up', picked_up_at: new Date().toISOString() }, 'Marked picked up')
    sendPickedUp(row.id)
  }

  // Fire the "delivered" email + SMS — spec §7quater Phase 1.
  // The card stays mounted after `advance` (it just re-renders in the new
  // status column), so we can safely fire-and-forget — no navigation to
  // race with. `await` both so any error surfaces in the console log.
  async function markDelivered() {
    await advance({ status: 'delivered', delivered_at: new Date().toISOString() }, 'Delivered')
    sendDelivered(row.id)
    try { await sendSms(row.id, 'delivered') } catch { /* ignore */ }
  }

  const stop = (e) => e.stopPropagation()
  const cls = 'ml-auto rounded-lg bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white'

  async function doCancel() {
    setConfirmCancel(false)
    await advance({ status: 'cancelled' }, 'Order cancelled')
  }

  if (row.status === 'booked') {
    return (
      <div className="ml-auto flex items-center gap-2">
        {confirmCancel ? (
          <button
            type="button"
            onClick={(e) => { stop(e); doCancel() }}
            className="rounded-lg bg-red px-3 py-1.5 text-[11px] font-extrabold text-white"
          >
            Tap again to confirm
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => { stop(e); setConfirmCancel(true) }}
            className="rounded-lg border border-red bg-white px-3 py-1.5 text-[11px] font-extrabold text-red hover:bg-red/5"
          >
            Cancel
          </button>
        )}
        <button type="button" onClick={(e) => { stop(e); markPickedUp() }} className="rounded-lg bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white">
          Mark picked up
        </button>
      </div>
    )
  }
  if (row.status === 'picked_up') {
    return (
      <button type="button" onClick={(e) => { stop(e); advance({ status: 'processing' }, 'Processing started') }} className={cls}>
        Start processing
      </button>
    )
  }
  if (row.status === 'processing' && !paid) {
    return (
      <button type="button" onClick={(e) => { stop(e); openPanel(row) }} className={cls}>
        Checkout
      </button>
    )
  }
  if (row.status === 'processing' && paid) {
    return (
      <button type="button" onClick={(e) => { stop(e); advance({ status: 'out_for_delivery' }, 'Out for delivery') }} className={cls}>
        Send out for delivery
      </button>
    )
  }
  if (row.status === 'out_for_delivery') {
    return (
      <button type="button" onClick={(e) => { stop(e); markDelivered() }} className={cls}>
        Mark delivered
      </button>
    )
  }
  return null
}

// ══════════════════════════════════════════════════════════════════════
// History (read-only)
// ══════════════════════════════════════════════════════════════════════

function HistoryView({ openPanel, reloadKey = 0 }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    (async () => {
      setLoading(true); setError('')
      try { setRows(await listArchivedOrders()) }
      catch (err) { setError(err?.message || 'Could not load history') }
      finally { setLoading(false) }
    })()
  }, [reloadKey])

  if (loading) return <div className="rounded-xl bg-white p-6 text-center text-[13px] text-ink-2">Loading…</div>
  if (error) return <div className="rounded-xl border border-red bg-red/10 p-4 text-[12px] font-bold text-red">{error}</div>
  if (rows.length === 0) return <div className="rounded-xl bg-white p-6 text-center text-[13px] italic text-ink-3">Nothing archived yet.</div>

  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-white">
      <table className="w-full text-[12px]">
        <thead className="bg-[#F9FAFB] text-ink-2">
          <tr>
            <Th>GL-#</Th><Th>Client</Th><Th>Service</Th><Th>Pickup</Th><Th>Delivered</Th><Th align="right">Total</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.order_number} onClick={() => openPanel(r)} className="cursor-pointer border-t border-line hover:bg-[#F9FAFB]">
              <td className="px-3 py-2 font-bricolage font-extrabold text-navy">
                <div className="flex items-center gap-2">
                  <HistoryPhotoThumb path={r.delivery_photo_path} />
                  <span>#{r.order_number}</span>
                </div>
              </td>
              <td className="px-3 py-2 text-navy">{r.customer_name || '—'}</td>
              <td className="px-3 py-2 text-ink-2">{SERVICE_LABEL[r.service] || r.service}</td>
              <td className="px-3 py-2 text-navy">{r.pickup_date ? shortDate(r.pickup_date) : '—'}</td>
              <td className="px-3 py-2 text-navy">{r.delivered_at ? shortDate(r.delivered_at.slice(0, 10)) : (r.status === 'cancelled' ? 'cancelled' : '—')}</td>
              <td className="px-3 py-2 text-right font-bold text-navy">{fmtMoney(r.final_total_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Th({ children, align }) {
  return <th className={`px-3 py-2 text-${align || 'left'} font-bold uppercase tracking-wider`}>{children}</th>
}

// 38×38 thumbnail rendered next to the order number in History.
function HistoryPhotoThumb({ path }) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    let alive = true
    if (!path) { setUrl(null); return () => { alive = false } }
    getDeliveryProofUrl(path).then((u) => { if (alive) setUrl(u) })
    return () => { alive = false }
  }, [path])
  if (!path) return null
  return (
    <img
      src={url || ''}
      alt=""
      width={38}
      height={38}
      className="h-[38px] w-[38px] rounded-md object-cover"
      style={{ border: '1px solid rgba(11,32,56,0.15)' }}
    />
  )
}

// Unused imports guard so tree-shake is honest — `isoDate` reserved for
// future "today" filters.
void isoDate
